import { parseBerlinDateTime } from './bookingDateTime';

export const BOOKING_TIME_RANGE_ERROR_MESSAGE = 'Bitte wähle eine gültige Buchungszeit.';

const createBookingPayloadError = (message = BOOKING_TIME_RANGE_ERROR_MESSAGE) => {
  const error = new Error(message);
  error.userFacing = true;
  return error;
};

export const combineBookingDateAndTime = (date, time) => {
  if (!date || !time) throw new Error('Date and time are required');

  const dateObj = parseBerlinDateTime(date, time);
  if (!dateObj || Number.isNaN(dateObj.getTime())) {
    throw new Error('Invalid date or time format');
  }

  return dateObj.toISOString();
};

export const getBookingDurationInHours = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;

  const [startHour, startMinute = 0] = startTime.split(':').map(Number);
  const [rawEndHour, endMinute = 0] = endTime.split(':').map(Number);
  const endHour = endTime === '00:00' ? 24 : rawEndHour;
  const startTotalMinutes = startHour * 60 + startMinute;
  const endTotalMinutes = endHour * 60 + endMinute;
  const duration = (endTotalMinutes - startTotalMinutes) / 60;

  return Number.isFinite(duration) && duration > 0 ? duration : 0;
};

const getBookingCheckoutEndDateTime = (date, startTime, endTime) => {
  const endsAtSelectedDayMidnight = endTime === '00:00' && startTime !== '00:00';
  return combineBookingDateAndTime(date, endsAtSelectedDayMidnight ? '24:00' : endTime);
};

export const getValidatedBookingDateTimeRange = (bookingData = {}) => {
  const { date, startTime, endTime } = bookingData;
  if (!date || !startTime || !endTime) {
    throw createBookingPayloadError();
  }

  let startDateTime;
  let endDateTime;
  try {
    startDateTime = combineBookingDateAndTime(date, startTime);
    endDateTime = getBookingCheckoutEndDateTime(date, startTime, endTime);
  } catch (error) {
    throw createBookingPayloadError();
  }

  const durationInHours = getBookingDurationInHours(startTime, endTime);
  const startMs = new Date(startDateTime).getTime();
  const endMs = new Date(endDateTime).getTime();

  if (
    !Number.isFinite(durationInHours) ||
    durationInHours <= 0 ||
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs) ||
    endMs <= startMs
  ) {
    throw createBookingPayloadError();
  }

  return {
    startDateTime,
    endDateTime,
    durationInHours,
  };
};

export const getBookingNumberOfGuests = ({ bookingData, isPackagePricing }) => {
  const raw =
    isPackagePricing && bookingData?.selectedPeopleGroupKey
      ? bookingData.selectedPeopleGroupKey
      : bookingData?.numberOfPeople;

  if (raw == null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
  if (typeof raw !== 'string') return null;

  const match = raw.match(/^(\d+)(?:-(\d+)|\+)?$/);
  if (!match) return null;

  const min = parseInt(match[1], 10);
  const max = match[2] ? parseInt(match[2], 10) : min;
  if (Number.isNaN(min)) return null;

  return Number.isFinite(max || min) ? (max || min) : null;
};

export const buildBookingCheckoutPayload = ({ bookingData, studioId, isPackagePricing }) => {
  if (!studioId) {
    throw createBookingPayloadError();
  }

  const {
    startDateTime,
    endDateTime,
    durationInHours,
  } = getValidatedBookingDateTimeRange(bookingData);

  const payload = {
    studioID: studioId,
    startDateTime,
    endDateTime,
    durationInHours,
    projectName: bookingData.projectName || '',
    projectDescription: bookingData.description || '',
    ...(bookingData.companyName && { companyName: bookingData.companyName }),
    ...(bookingData.creativePackages?.length > 0 && {
      creativePackages: bookingData.creativePackages,
    }),
    ...(bookingData.bookedEquipment?.length > 0 && {
      bookedEquipment: bookingData.bookedEquipment,
    }),
  };

  if (isPackagePricing && bookingData?.packageId) {
    payload.packageId = bookingData.packageId;
    if (bookingData.packageName) payload.packageName = bookingData.packageName;
    if (bookingData.packageDurationHours) {
      payload.packageDurationHours = bookingData.packageDurationHours;
    }
  }

  const guests = getBookingNumberOfGuests({ bookingData, isPackagePricing });
  if (guests) payload.numberOfGuests = guests;

  return payload;
};

export const buildBookingPayloadSignature = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return JSON.stringify(payload);
  }

  const {
    projectName,
    projectDescription,
    companyName,
    ...reservationRelevantPayload
  } = payload;

  return JSON.stringify(reservationRelevantPayload);
};
